import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Typography, Tag, Progress, Form, Input, Button, Select, App } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import CrudPage from '../../../components/common/CrudPage';
import { voucherService } from '../../../services/next';
import { exportRowsToExcel } from '../../../utils/utility';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: 'Hiển thị', value: 1 },
  { label: 'Ẩn', value: 0 },
];

// "Loại giảm giá" (cột type) - khớp với cms.next
const DISCOUNT_TYPE_OPTIONS = [
  { label: 'Theo tập khách hàng', value: 1 },
  { label: 'Sinh nhật (áp dụng riêng cho sinh nhật của từng người dùng)', value: 2 },
  { label: 'Theo Mã khách hàng', value: 3 },
];

// "Đơn vị" (cột unit) - khớp với cms.next: 1 = VND, 2 = %
const UNIT_OPTIONS = [
  { label: 'VND', value: 1 },
  { label: '%', value: 2 },
];

const YES_NO_OPTIONS = [
  { label: 'Có', value: 1 },
  { label: 'Không', value: 0 },
];

// Loại target lưu ở bảng vouchers_targets — khớp VoucherController của cms.next.
// LƯU Ý: cms.next ghi audiences CÙNG type 3 với topic, nên khi đọc ngược phải phân biệt
// bằng danh sách code audience (xem splitTopicAndAudience bên dưới).
const TARGET_TYPE = { platform: 1, version: 2, topic: 3, audience: 3 };

// service_ids / premium_type_ids: cms.next lưu JSON array (json_encode) trong cột varchar.
// Chấp nhận thêm dạng "1,2,3" của dữ liệu cũ để không mất lựa chọn khi mở form sửa.
const parseIdList = (raw) => {
  if (raw === null || raw === undefined || raw === '' || raw === 'null') return [];
  if (Array.isArray(raw)) return raw.map(Number).filter((v) => !Number.isNaN(v));
  const str = String(raw).trim();
  if (str.startsWith('[')) {
    try {
      const parsed = JSON.parse(str);
      return Array.isArray(parsed) ? parsed.map(Number).filter((v) => !Number.isNaN(v)) : [];
    } catch (e) {
      return [];
    }
  }
  return str.split(',').map((v) => Number(v.trim())).filter((v) => !Number.isNaN(v));
};

const stringifyIdList = (value) => {
  if (!Array.isArray(value) || value.length === 0) return null;
  return JSON.stringify(value.map(Number));
};

const searchByLabel = (input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase());

/**
 * "Gói áp dụng" phụ thuộc "Dịch vụ áp dụng" — bám theo VoucherController@getPremiumTypes của
 * cms.next: chỉ hiện premium_type có `premium_groups` nằm trong service_ids đang chọn
 * (metadata đã trả sẵn group_id nên lọc thẳng ở client, không cần gọi lại API mỗi lần đổi).
 * Chưa chọn dịch vụ nào thì hiện toàn bộ gói.
 */
const PremiumTypeSelect = ({ options }) => {
  const form = Form.useFormInstance();
  const serviceIds = Form.useWatch('service_ids', form);
  const selected = Form.useWatch('premium_type_ids', form);

  const filtered = useMemo(() => {
    if (!serviceIds || !serviceIds.length) return options;
    const picked = new Set(serviceIds.map(String));
    return options.filter((o) => picked.has(String(o.group_id)));
  }, [options, serviceIds]);

  // Đổi dịch vụ thì bỏ luôn các gói không còn thuộc dịch vụ nào đang chọn, tránh gửi lên
  // premium_type_ids lạc khỏi service_ids (cms.next cũng loại chúng khi render lại form).
  useEffect(() => {
    if (!selected || !selected.length) return;
    const allowed = new Set(filtered.map((o) => String(o.value)));
    const kept = selected.filter((v) => allowed.has(String(v)));
    if (kept.length !== selected.length) form.setFieldValue('premium_type_ids', kept);
  }, [filtered, selected, form]);

  return (
    <Form.Item name="premium_type_ids" label="Gói áp dụng">
      <Select
        mode="multiple"
        showSearch
        allowClear
        filterOption={searchByLabel}
        placeholder={serviceIds && serviceIds.length ? 'Chọn gói premium' : 'Chọn dịch vụ trước để lọc gói'}
        options={filtered}
        notFoundContent="Dịch vụ đang chọn chưa có gói premium nào"
      />
    </Form.Item>
  );
};

// Cột file .xlsx bám theo VoucherController::export của cms.next, chỉ chèn thêm "Tổng số mã"
// (cột `total`) vì bản cũ đặt tên "Tổng số lần sử dụng" nhưng lại đổ `number_of_use`,
// nhìn file không biết chương trình phát hành bao nhiêu mã.
const EXPORT_HEADERS = [
  '#',
  'Tên mã',
  'Trạng thái',
  'Đối tác',
  'CTKM',
  'Mã Voucher',
  'Tổng số mã',
  'Tổng số lần sử dụng',
  'Tổng số lần dùng mỗi user',
  'Thời gian phát hành',
  'Thời gian kết thúc',
];

// Mỗi voucher còn kèm bản ghi bảng vouchers_targets nên không xin một phát toàn bộ,
// chia trang rồi ghép lại cho nhẹ cả API lẫn trình duyệt.
const EXPORT_PAGE_SIZE = 1000;

const statusLabel = (value) => {
  const matched = STATUS_OPTIONS.find((o) => Number(o.value) === Number(value));
  return matched ? matched.label : (value ?? '');
};

const formatExportTime = (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '');

const QuanLyMaGiamGia = () => {
  const { notification } = App.useApp();
  const [searchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);

  const { data: meta } = useQuery({
    queryKey: ['voucherMetadata'],
    queryFn: async () => {
      const res = await voucherService.getMetadata();
      return res?.data || {};
    },
  });

  const ctkmOptions = useMemo(() => meta?.ctkms || [], [meta]);
  const serviceOptions = useMemo(() => meta?.services || [], [meta]);
  const platformOptions = useMemo(() => meta?.topics?.platform || [], [meta]);
  const versionOptions = useMemo(() => meta?.topics?.version || [], [meta]);
  const topicOptions = useMemo(() => meta?.topics?.topic || [], [meta]);
  const audienceOptions = useMemo(() => meta?.audiences || [], [meta]);

  // Nhãn kèm tên dịch vụ để lúc CHƯA chọn dịch vụ (hiện toàn bộ gói) vẫn phân biệt được gói
  // nào của dịch vụ nào. Việc lọc theo dịch vụ do PremiumTypeSelect đảm nhiệm.
  const premiumTypeOptions = useMemo(() => {
    const serviceName = new Map((meta?.services || []).map((s) => [String(s.value), s.label]));
    return (meta?.premiumTypes || []).map((p) => {
      const group = serviceName.get(String(p.group_id));
      return { ...p, label: group ? `[${group}] ${p.label}` : p.label };
    });
  }, [meta]);

  const audienceCodes = useMemo(() => new Set(audienceOptions.map((a) => String(a.value))), [audienceOptions]);

  // targets type 3 gộp cả topic lẫn audience -> tách lại theo danh sách code audience
  const splitTopicAndAudience = useMemo(
    () => (targets) => {
      const topic = [];
      const audience = [];
      targets
        .filter((t) => Number(t.type) === TARGET_TYPE.topic)
        .forEach((t) => (audienceCodes.has(String(t.target)) ? audience : topic).push(t.target));
      return { topic, audience };
    },
    [audienceCodes]
  );

  const ctkmLabel = useMemo(() => {
    const map = new Map(ctkmOptions.map((c) => [String(c.value), c.label]));
    return (id) => map.get(String(id)) || id || '-';
  }, [ctkmOptions]);

  const columns = useMemo(() => [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'HÌNH ẢNH', key: 'image_url', width: 100, render: (_, r) => r.image_url ? <img src={r.image_url} alt="img" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }} /> : '-' },
    { title: 'ĐỐI TÁC', dataIndex: 'partner_id', key: 'partner_id', width: 120 },
    { title: 'MÃ GIẢM GIÁ', dataIndex: 'code', key: 'code', width: 120, render: (t) => <Text strong code>{t}</Text> },
    { title: 'TÊN MÃ', dataIndex: 'title', key: 'title', width: 200 },
    { title: 'MÔ TẢ', dataIndex: 'description', key: 'description', width: 200, ellipsis: true },
    { title: 'CHƯƠNG TRÌNH KM', dataIndex: 'ctkm_id', key: 'ctkm_id', width: 180, ellipsis: true, render: (v) => ctkmLabel(v) },
    { title: 'ĐỐI TƯỢNG ÁP DỤNG', key: 'targets', width: 160, ellipsis: true, render: (_, r) => {
        if (Array.isArray(r.targets) && r.targets.length) {
            return <Text style={{ fontSize: 12 }}>{r.targets.map((t) => t.target).join(', ')}</Text>;
        }
        return '-';
    }},
    { title: 'SỬ DỤNG', key: 'usage', width: 120, render: (_, r) => {
        const percent = r.total > 0 ? Math.round((r.number_of_use / r.total) * 100) : 0;
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={{ fontSize: 12 }}>{r.number_of_use} / {r.total}</Text>
                <Progress percent={percent} size="small" status={percent >= 100 ? 'exception' : 'active'} />
            </div>
        );
    }},
    { title: 'THỜI GIAN PHÁT HÀNH', dataIndex: 'publish_time', key: 'publish_time', width: 150, render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-' },
    { title: 'THỜI GIAN KẾT THÚC', dataIndex: 'end_time', key: 'end_time', width: 150, render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-' },
    {
        title: 'TRẠNG THÁI',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (s) => {
            const map = { 0: ['default','ẨN'], 1: ['green','HIỂN THỊ'] };
            const [c,l] = map[s] || ['default', s];
            return <Tag color={c}>{l}</Tag>;
        }
    },
  ], [ctkmLabel]);

  // Thứ tự trường bám theo popup Tạo mới / Sửa voucher của cms.next (vouchers/create.blade.php).
  // isEditMode: cms.next khoá Tiền tố / Độ dài và bỏ hẳn Số lượng mã ở form sửa (edit.blade.php)
  const formFields = useCallback(({ isEditMode }) => [
    { type: 'divider', label: 'Thông tin cơ bản' },
    { name: 'title', label: 'Tên mã giảm giá', type: 'input', span: 24, rules: [{ required: true, message: 'Nhập tên!' }] },
    { name: 'partner_id', label: 'Đối tác', type: 'input', span: 12 },
    // Lúc tạo, code do API sinh ra từ tiền tố + độ dài (giống cms.next: create.blade.php không có
    // ô Code). Chỉ form sửa mới cho chỉnh tay đúng mã của bản ghi đang mở.
    ...(isEditMode
      ? [{ name: 'code', label: 'Mã giảm giá (Code)', type: 'input', span: 12, rules: [({ getFieldValue }) => ({ required: getFieldValue('is_template') !== 1, message: 'Nhập mã!' })] }]
      : []),
    // Mã đã phát hành theo tiền tố/độ dài cũ nên sửa 2 trường này sẽ làm lệch dải mã -> chỉ cho nhập lúc tạo
    // cms.next bắt buộc Tiền tố khi tạo (VouchersRequest::rules) vì code sinh từ tiền tố
    { name: 'prefix', label: 'Tiền tố', type: 'input', span: 12, propsInput: { disabled: isEditMode }, rules: isEditMode ? [] : [{ required: true, message: 'Nhập tiền tố!' }] },
    {
      name: 'length',
      label: 'Độ dài mã code (sau tiền tố)',
      type: 'number',
      span: 12,
      placeholder: 'VD: Tiền tố KM1 + độ dài 3 => KM1ABC',
      propsInput: { disabled: isEditMode },
      // Sinh nhiều mã mà không có phần ngẫu nhiên thì mọi mã sẽ trùng nhau
      rules: isEditMode
        ? []
        : [({ getFieldValue }) => ({
            required: Number(getFieldValue('total')) > 1,
            message: 'Tạo nhiều hơn 1 mã thì phải nhập độ dài phần ngẫu nhiên!',
          })],
    },
    {
      name: 'ctkm_id',
      label: 'Chương trình giảm giá',
      type: 'select',
      span: 24,
      options: ctkmOptions,
      placeholder: 'Chọn chương trình khuyến mại',
      propsInput: { showSearch: true, allowClear: true, filterOption: searchByLabel },
      rules: [{ required: true, message: 'Chọn chương trình khuyến mại!' }],
    },
    { name: 'description', label: 'Mô tả mã giảm giá', type: 'textarea', span: 24, propsInput: { rows: 2 } },
    { name: 'image_url', label: 'Ảnh', type: 'upload-cdn', span: 24, uploadFolder: 'voucher' },

    { type: 'divider', label: 'Cấu hình giảm giá' },
    { name: 'type', label: 'Loại giảm giá', type: 'select', options: DISCOUNT_TYPE_OPTIONS, defaultValue: 1, span: 12, rules: [{ required: true, message: 'Chọn loại giảm giá!' }] },
    { name: 'unit', label: 'Đơn vị', type: 'select', options: UNIT_OPTIONS, defaultValue: 1, span: 12, rules: [{ required: true, message: 'Chọn đơn vị!' }] },
    { name: 'discount_value', label: 'Giá trị giảm giá (phần thập phân dùng dấu chấm, vd 20.5)', type: 'number', span: 24, rules: [{ required: true, message: 'Nhập giá trị giảm giá!' }] },
    // Số lượng mã chỉ có ở form tạo mới (cms.next không cho sửa tổng số mã đã phát hành)
    ...(isEditMode
      ? []
      : [{
          name: 'total',
          label: 'Số lượng mã (sinh ra bấy nhiêu mã, mỗi mã 1 bản ghi)',
          type: 'number',
          span: 8,
          defaultValue: 1,
          propsInput: { min: 1 },
          rules: [{ required: true, message: 'Nhập số lượng!' }],
        }]),
    { name: 'number_of_use', label: 'Lượt sử dụng', type: 'number', span: isEditMode ? 12 : 8 },
    { name: 'number_per_user', label: 'Lượt sử dụng mỗi user', type: 'number', span: isEditMode ? 12 : 8 },
    { name: 'date_from_receive', label: 'Kể từ ngày được nhận mã', type: 'number', span: 24 },

    { type: 'divider', label: 'Phạm vi áp dụng' },
    {
      name: 'service_ids',
      label: 'Dịch vụ áp dụng',
      type: 'select',
      span: 12,
      options: serviceOptions,
      placeholder: 'Chọn dịch vụ',
      propsInput: { mode: 'multiple', showSearch: true, allowClear: true, filterOption: searchByLabel },
    },
    {
      // Select phụ thuộc field khác -> render riêng, RenderForm chỉ nhận options tĩnh
      type: 'html',
      span: 12,
      content: <PremiumTypeSelect options={premiumTypeOptions} />,
    },

    { type: 'divider', label: 'Thời gian' },
    { name: 'publish_time', label: 'Thời gian phát hành', type: 'date', span: 12, format: 'DD/MM/YYYY HH:mm:ss', propsInput: { showTime: true, format: 'DD/MM/YYYY HH:mm:ss' } },
    { name: 'end_time', label: 'Thời gian kết thúc', type: 'date', span: 12, format: 'DD/MM/YYYY HH:mm:ss', propsInput: { showTime: true, format: 'DD/MM/YYYY HH:mm:ss' } },

    { type: 'divider', label: 'Target (đối tượng áp dụng)' },
    {
      name: 'platform',
      label: 'Platform',
      type: 'select',
      span: 12,
      options: platformOptions,
      placeholder: 'Tất cả',
      propsInput: { mode: 'multiple', showSearch: true, allowClear: true, filterOption: searchByLabel },
    },
    {
      name: 'version',
      label: 'Version (v9.0:v9.3 = từ 9.0 đến 9.3, v9.0:999 = từ 9.0 trở lên, v9.2 = đúng 9.2)',
      type: 'select',
      span: 12,
      options: versionOptions,
      placeholder: 'Tất cả',
      propsInput: { mode: 'tags', showSearch: true, allowClear: true, filterOption: searchByLabel },
    },
    {
      name: 'topic',
      label: 'Topic',
      type: 'select',
      span: 12,
      options: topicOptions,
      placeholder: 'Tất cả',
      propsInput: { mode: 'multiple', showSearch: true, allowClear: true, filterOption: searchByLabel },
    },
    {
      name: 'audiences',
      label: 'Audiences (tập khách hàng)',
      type: 'select',
      span: 12,
      options: audienceOptions,
      placeholder: 'Tất cả',
      propsInput: { mode: 'multiple', showSearch: true, allowClear: true, filterOption: searchByLabel },
    },

    { type: 'divider', label: 'Khác' },
    { name: 'allow_share', label: 'Cho phép chia sẻ', type: 'select', options: YES_NO_OPTIONS, defaultValue: 0, span: 12 },
    {
      // Giống cms.next: Link share chỉ hiện khi "Cho phép chia sẻ" = Có
      type: 'html',
      span: 12,
      content: (
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.allow_share !== cur.allow_share}>
          {({ getFieldValue }) =>
            Number(getFieldValue('allow_share')) === 1 ? (
              <Form.Item name="link_share" label="Link share">
                <Input placeholder="Link share" allowClear />
              </Form.Item>
            ) : null
          }
        </Form.Item>
      ),
    },
    { name: 'is_template', label: 'Voucher mặc định (Template)', type: 'select', options: YES_NO_OPTIONS, defaultValue: 0, span: 12 },
    { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 1, span: 12 },
  ], [ctkmOptions, serviceOptions, premiumTypeOptions, platformOptions, versionOptions, topicOptions, audienceOptions]);

  // Bộ lọc bám theo form tìm kiếm của cms.next (vouchers/index.blade.php)
  const filterFields = useMemo(() => [
    {
      label: 'Chương trình khuyến mại',
      value: 'ctkm_id',
      options: [{ label: 'Tất cả chương trình', value: '' }, ...ctkmOptions],
    },
    { label: 'Đối tác', value: 'partner_id', type: 'input' },
    { label: 'Mã code', value: 'code', type: 'input' },
    { label: 'Từ ngày phát hành', value: 'publish_time', type: 'date' },
    { label: 'Đến ngày kết thúc', value: 'end_time', type: 'date' },
    {
      label: 'Là Voucher Template',
      value: 'is_template',
      options: [
        { label: 'Tất cả', value: '' },
        ...YES_NO_OPTIONS,
      ],
    },
    {
      label: 'Trạng thái',
      value: 'status',
      options: [
        { label: 'Tất cả trạng thái', value: '' },
        ...STATUS_OPTIONS,
      ],
    },
  ], [ctkmOptions]);

  // Bộ lọc nằm ở URL (CrudPage/PageHeader đẩy lên searchParams) nên đọc thẳng từ đó
  // để file export khớp đúng những gì đang hiển thị trên bảng.
  const buildExportParams = useCallback(() => {
    const params = {};
    filterFields.forEach((f) => {
      const value = searchParams.get(f.value);
      if (value) params[f.value] = value;
    });
    const search = searchParams.get('search');
    if (search) params.search = search;
    return params;
  }, [filterFields, searchParams]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      notification.info({ message: 'Đang xuất dữ liệu...' });
      const baseParams = buildExportParams();

      const items = [];
      let page = 1;
      let totalItem = 0;
      // Lặp tới khi gom đủ totalItem; chunk rỗng thì dừng để không quay vòng vô tận
      // nếu API trả thiếu so với totalItem.
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const res = await voucherService.getList({ ...baseParams, page, limit: EXPORT_PAGE_SIZE });
        const chunk = Array.isArray(res?.data) ? res.data : [];
        totalItem = Number(res?.totalItem) || 0;
        items.push(...chunk);
        if (chunk.length === 0 || items.length >= totalItem) break;
        page += 1;
      }

      if (items.length === 0) {
        notification.warning({ message: 'Không có mã giảm giá nào khớp bộ lọc để xuất' });
        return;
      }

      const rows = items.map((item, index) => [
        index + 1,
        item.title ?? '',
        statusLabel(item.status),
        item.partner_id ?? '',
        ctkmLabel(item.ctkm_id),
        item.code ?? '',
        item.total ?? '',
        item.number_of_use ?? '',
        item.number_per_user ?? '',
        formatExportTime(item.publish_time),
        formatExportTime(item.end_time),
      ]);

      await exportRowsToExcel(EXPORT_HEADERS, rows, `ma_giam_gia_${dayjs().format('YYYY-MM-DD')}`);
      notification.success({ message: `Xuất ${rows.length} mã giảm giá thành công` });
    } catch (error) {
      notification.error({ message: 'Lỗi khi xuất file', description: error?.message });
    } finally {
      setIsExporting(false);
    }
  }, [buildExportParams, ctkmLabel, notification]);

  const headerActions = (
    <Button icon={<DownloadOutlined />} loading={isExporting} onClick={handleExport}>
      Export .xlsx
    </Button>
  );

  // record (DB) -> form
  const mapDataToForm = (record) => {
    const targets = Array.isArray(record.targets) ? record.targets : [];
    const { topic, audience } = splitTopicAndAudience(targets);
    return {
      // Model trả is_template dạng boolean -> chuẩn hoá về 1/0 để Select hiển thị đúng
      is_template: record.is_template === true || record.is_template === 1 ? 1 : 0,
      service_ids: parseIdList(record.service_ids),
      premium_type_ids: parseIdList(record.premium_type_ids),
      platform: targets.filter((t) => Number(t.type) === TARGET_TYPE.platform).map((t) => t.target),
      version: targets.filter((t) => Number(t.type) === TARGET_TYPE.version).map((t) => t.target),
      topic,
      audiences: audience,
    };
  };

  // form -> payload gửi API
  const mapFormToData = (payload) => {
    const targets = [];
    (payload.platform || []).forEach((v) => targets.push({ type: TARGET_TYPE.platform, target: String(v) }));
    (payload.version || []).forEach((v) => targets.push({ type: TARGET_TYPE.version, target: String(v) }));
    (payload.topic || []).forEach((v) => targets.push({ type: TARGET_TYPE.topic, target: String(v) }));
    (payload.audiences || []).forEach((v) => targets.push({ type: TARGET_TYPE.audience, target: String(v) }));

    return {
      is_template: payload.is_template ? 1 : 0,
      service_ids: stringifyIdList(payload.service_ids),
      premium_type_ids: stringifyIdList(payload.premium_type_ids),
      targets,
      // Các field chỉ dùng ở FE — API validate strict nên phải bỏ hẳn
      platform: undefined,
      version: undefined,
      topic: undefined,
      audiences: undefined,
    };
  };

  return (
    <CrudPage
      title="QUẢN LÝ MÃ GIẢM GIÁ (VOUCHER)"
      subTitle="Tạo và quản lý các mã giảm giá cho người dùng"
      itemsName="Mã giảm giá"
      queryKey="voucherList"
      service={voucherService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm theo tiêu đề..."
      addButtonLabel="Tạo Mới"
      renderHeaderActions={headerActions}
      formConfig={{ width: 900 }}
      mapDataToForm={mapDataToForm}
      mapFormToData={mapFormToData}
    />
  );
};

export default QuanLyMaGiamGia;
